import firebase from "firebase/app";

export const tutorCollectionKey = "tutors";
export const tutorConfigCollectionKey = "tutorConfigs";

export type TutorAuthority =
  | "owner"
  | "admin"
  | "supervisor"
  | "employee"
  | "privateTutor"
  | "general";

export type Tutor = {
  ref: firebase.firestore.DocumentReference;
  data: TutorData;
};

export type TutorData = {
  id: string; // uid
  name: string; // スタッフ名
  school: firebase.firestore.DocumentReference; // 所属している塾への reference (security 設計上必要)
  allowEmailNotify?: boolean; // 入退室通知メールの送信をするかどうか
  allowBadgeEmailNotify?: boolean; // バッジ獲得時のメールの送信をするかどうか
  allowBulkUpdateTutors?: boolean; //tutorsの一括更新が可能か *admin権限のみで評価(ownerは常時可能、admin未満は常時不可能)
  prompt?: { docId: string; name: string; text: string };
  favoritePromptRefPaths?: string[]; // スクールAIホームでお気に入り登録したアプリ(prompt)のrefPath。先頭ほど優先度高
  schoolAiLastLoginAt?: number; // school-ai への最終ログイン日時(unixtime)。ログイン時に本人が更新する
};

export type TutorConfig = {
  ref: firebase.firestore.DocumentReference;
  data: TutorConfigData;
};

export type TutorConfigData = {
  id: string; // uid
  authority: TutorAuthority; // 権限
  classrooms: firebase.firestore.DocumentReference[]; // アクセス権限のある教室一覧の reference
  passwordChangeRequired?: boolean; // 初回パスワードを更新する必要があるか
  passwordLoginDisabled?: boolean; // パスワードログインを無効にするか
  passwordCampaignId?: string; // 全先生のパスワード変更施策で、変更を済ませたキャンペーンの ID（サーバーだけが書く）
  passwordCompletedAt?: number; // その変更を済ませた日時（ミリ秒）
  privateTutorSubmissionEmailEnabled?: boolean; // 家庭教師への提出・生徒メッセージ通知メール。既存の未設定アカウントは送信を継続
  privateTutorMessagingAllowed?: boolean; // 家庭教師から担当生徒へのメッセージ送信。true の場合のみ許可（新着通知の受信は制限しない）
  recessTime?: number; // 休会にした日時(unixtime 秒)。0 または未設定は在籍。生徒の recessTime と同じ持ち方
};

// 先生が休会中か。生徒の判定（recessTime > 0）と同じ基準
export function isTutorInRecess(
  configData: Pick<TutorConfigData, "recessTime"> | null | undefined
): boolean {
  const recessTime = configData?.recessTime;
  return typeof recessTime === "number" && recessTime > 0;
}

// 在籍/休会の切り替えで tutorConfig に書く差分。変化が無ければ null。
// 休会にするときは now を記録し、在籍に戻すときは生徒と同じく 0 にする
export function tutorRecessTransition(
  currentRecessTime: number | undefined,
  nextInRecess: boolean,
  now: number
): { recessTime: number } | null {
  const currentlyInRecess = isTutorInRecess({ recessTime: currentRecessTime });
  if (currentlyInRecess === nextInRecess) return null;
  return { recessTime: nextInRecess ? now : 0 };
}

// tutor reference の [schoolDocId, tutorDocId] を返す
export function getDocIdsOfTutorRef(
  tutorRef: firebase.firestore.DocumentReference
): [string, string] {
  if (tutorRef.parent.id !== tutorCollectionKey) {
    throw new Error("given data is not tutor reference");
  }

  if (!tutorRef.parent.parent) {
    return ["", ""];
  }
  const schoolRef = tutorRef.parent.parent;
  return [schoolRef.id, tutorRef.id];
}

// tutor の [schoolDocId, tutorDocId] を返す
export function getDocIdsOfTutor(tutor: Tutor): [string, string] {
  return getDocIdsOfTutorRef(tutor.ref);
}

// tutor reference の [schoolDocId, tutorDocId] を返す
export function getDocIdsOfTutorConfigRef(
  configRef: firebase.firestore.DocumentReference
): [string, string] {
  if (configRef.parent.id !== tutorConfigCollectionKey) {
    throw new Error("given data is not tutor config reference");
  }

  if (!configRef.parent.parent) {
    return ["", ""];
  }
  const schoolRef = configRef.parent.parent;
  return [schoolRef.id, configRef.id];
}

export function getSchoolRefFromTutorRef(
  tutorRef: firebase.firestore.DocumentReference
): firebase.firestore.DocumentReference {
  if (tutorRef.parent.id !== tutorCollectionKey) {
    throw new Error("given data is not tutor reference");
  }
  return tutorRef.parent.parent!;
}

export function convertToTutor(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): Tutor {
  const returnData: TutorData = {
    id: data.id,
    name: data.name,
    school: data.school
  };
  if (data.allowEmailNotify) {
    returnData.allowEmailNotify = data.allowEmailNotify;
  }
  if (data.allowBadgeEmailNotify) {
    returnData.allowBadgeEmailNotify = data.allowBadgeEmailNotify;
  }
  if (data.selectedPrompt) {
    returnData.prompt = data.selectedPrompt;
  }
  if (data.allowBulkUpdateTutors) {
    returnData.allowBulkUpdateTutors = data.allowBulkUpdateTutors;
  }
  if (data.favoritePromptRefPaths) {
    returnData.favoritePromptRefPaths = data.favoritePromptRefPaths;
  }
  if (typeof data.schoolAiLastLoginAt === "number") {
    returnData.schoolAiLastLoginAt = data.schoolAiLastLoginAt;
  }

  return {
    ref,
    data: returnData
  };
}

export function convertToTutorConfig(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): TutorConfig {
  const returnData: TutorConfigData = {
    id: data.id,
    authority: data.authority,
    classrooms: data.classrooms,
    passwordChangeRequired: data.passwordChangeRequired ?? false,
    passwordLoginDisabled: data.passwordLoginDisabled ?? false
  };
  if (typeof data.passwordCampaignId === "string") {
    returnData.passwordCampaignId = data.passwordCampaignId;
  }
  if (typeof data.passwordCompletedAt === "number") {
    returnData.passwordCompletedAt = data.passwordCompletedAt;
  }
  if (typeof data.privateTutorSubmissionEmailEnabled === "boolean") {
    returnData.privateTutorSubmissionEmailEnabled =
      data.privateTutorSubmissionEmailEnabled;
  }
  if (typeof data.privateTutorMessagingAllowed === "boolean") {
    returnData.privateTutorMessagingAllowed = data.privateTutorMessagingAllowed;
  }
  if (typeof data.recessTime === "number") {
    returnData.recessTime = data.recessTime;
  }

  return {
    ref,
    data: returnData
  };
}

export type PasswordLoginConfigPatch = {
  passwordLoginDisabled: boolean;
  passwordChangeRequired?: boolean;
};

// 個別更新と同じ遷移。next が true のときは初期パスワード変更を出さない。
// true から false に戻すときは、次のパスワードログインで初期パスワード変更を出す。
// false のまま false のときは passwordChangeRequired を付けない。
export function passwordLoginTransition(
  currentDisabled: boolean,
  nextDisabled: boolean
): PasswordLoginConfigPatch {
  if (nextDisabled) {
    return {
      passwordLoginDisabled: true,
      passwordChangeRequired: false
    };
  }
  if (currentDisabled) {
    return {
      passwordLoginDisabled: false,
      passwordChangeRequired: true
    };
  }
  return { passwordLoginDisabled: false };
}

export function passwordLoginPatchChangesStoredState(
  current: {
    passwordLoginDisabled: boolean;
    passwordChangeRequired: boolean;
  },
  patch: PasswordLoginConfigPatch
): boolean {
  if (current.passwordLoginDisabled !== patch.passwordLoginDisabled) {
    return true;
  }
  return (
    patch.passwordChangeRequired !== undefined &&
    current.passwordChangeRequired !== patch.passwordChangeRequired
  );
}
